import { PostOrdersRequestBody } from "types/api/orders/postOrders";
import { models } from "types/api/viewModels";
import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { convertToTitleCase } from "utils/string.ts";

interface ShipToToAddressReturn {
	address?: models["AddressViewModel"];
	shipToId?: string | null;
}

export const convertShipToToAddress = (shipTo: models["ShipToViewModel"]): ShipToToAddressReturn => ({
	address: shipTo.address ?? undefined,
	shipToId: shipTo.shipToId ?? undefined
});

export const convertNewOrderToShipTo = (newOrder: PostOrdersRequestBody): models["ShipToViewModel"] | undefined =>
	newOrder.shipToId || (newOrder.shipToAddress && Object.keys(newOrder.shipToAddress).length > 0)
		? {
				shipToId: newOrder.shipToId,
				address: newOrder.shipToAddress
			}
		: undefined;

export const formatAddress = (address?: models["AddressViewModel"]) => {
	if (!address) return UnavailableDataPlaceholderText;

	const name = address.name ? convertToTitleCase(address.name) : "";
	const lines = [address.line1, address.line2, address.line3].filter(Boolean).map((line) => convertToTitleCase(line));

	const cityStateZip =
		[convertToTitleCase(address.city), address.state?.toUpperCase()].filter(Boolean).join(", ") +
		(address.zip ? ` ${convertToTitleCase(address.zip)}` : "");

	return [name, ...lines, cityStateZip].filter(Boolean).join("\n");
};

const getComparableString = (shipTo: models["ShipToViewModel"]): string => {
	return (
		shipTo.address?.line1?.trim() ??
		shipTo.address?.line2?.trim() ??
		shipTo.address?.line3?.trim() ??
		shipTo.address?.city?.trim() ??
		shipTo.address?.state?.trim() ??
		shipTo.address?.zip?.trim() ??
		""
	);
};

export const sortAddresses = (addresses: models["ShipToViewModel"][]): models["ShipToViewModel"][] => {
	const sorted = addresses
		.filter((address, index, self) => {
			const hasOnlyUndefinedValues =
				address.address && Object.values(address.address).every((value) => value === undefined);

			return address.address && !hasOnlyUndefinedValues && address.isActive;
		})
		.sort((a, b) => {
			const aComparable = getComparableString(a).toLowerCase();
			const bComparable = getComparableString(b).toLowerCase();

			if (aComparable < bComparable) return -1;
			if (aComparable > bComparable) return 1;
			return 0;
		});
	return sorted;
};
